"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Eye, EyeOff, HelpCircle, Mail, ScanFace } from "lucide-react";
import { Tooltip, CircularProgress } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

import { useStandardAuth } from './hooks/useStandardAuth';
import { useRememberMe } from './utils/cookieManager';
import { clearUserSessionFully, getSessionToken, saveUserSession, getHomeRoute } from './utils/sessionManager';
import {
    DeviceAccountCard, LiveAccountCard, loadDeviceAccountCards, readDeviceAccounts, rememberDeviceAccounts, removeDeviceAccount,
    switchToDeviceAccount
} from './utils/deviceAccounts';
import { useKeyboardLayoutConverter } from './utils/keyboardLayoutConverter';
import { API_CONFIG } from './config/authConfig';
import PasswordResetDialogWithWA from './Components/PasswordResetDialogWithWA';
import MailLoginDialog from './Components/MailLoginDialog';
import ActiveAccountsPanel from './Components/ActiveAccountsPanel';
import FaceIdDialog from './Components/FaceIdDialog';

import logo from '../media/logob.png';

// ============ Pupil Component ============
interface PupilProps {
    size?: number;
    maxDistance?: number;
    pupilColor?: string;
    forceLookX?: number;
    forceLookY?: number;
}

const Pupil = ({
    size = 12,
    maxDistance = 5,
    pupilColor = "black",
    forceLookX,
    forceLookY
}: PupilProps) => {
    const [mouseX, setMouseX] = useState<number>(0);
    const [mouseY, setMouseY] = useState<number>(0);
    const pupilRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            setMouseX(e.clientX);
            setMouseY(e.clientY);
        };

        window.addEventListener("mousemove", handleMouseMove);

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
        };
    }, []);

    const calculatePupilPosition = () => {
        if (!pupilRef.current) return { x: 0, y: 0 };

        if (forceLookX !== undefined && forceLookY !== undefined) {
            return { x: forceLookX, y: forceLookY };
        }

        const pupil = pupilRef.current.getBoundingClientRect();
        const pupilCenterX = pupil.left + pupil.width / 2;
        const pupilCenterY = pupil.top + pupil.height / 2;

        const deltaX = mouseX - pupilCenterX;
        const deltaY = mouseY - pupilCenterY;
        const distance = Math.min(Math.sqrt(deltaX ** 2 + deltaY ** 2), maxDistance);

        const angle = Math.atan2(deltaY, deltaX);
        const x = Math.cos(angle) * distance;
        const y = Math.sin(angle) * distance;

        return { x, y };
    };

    const pupilPosition = calculatePupilPosition();

    return (
        <div
            ref={pupilRef}
            className="rounded-full"
            style={{
                width: `${size}px`,
                height: `${size}px`,
                backgroundColor: pupilColor,
                transform: `translate(${pupilPosition.x}px, ${pupilPosition.y}px)`,
                transition: 'transform 0.1s ease-out',
            }}
        />
    );
};


// ============ EyeBall Component ============
interface EyeBallProps {
    size?: number;
    pupilSize?: number;
    maxDistance?: number;
    eyeColor?: string;
    pupilColor?: string;
    isBlinking?: boolean;
    forceLookX?: number;
    forceLookY?: number;
}

const EyeBall = ({
    size = 48,
    pupilSize = 16,
    maxDistance = 10,
    eyeColor = "white",
    pupilColor = "black",
    isBlinking = false,
    forceLookX,
    forceLookY
}: EyeBallProps) => {
    const [mouseX, setMouseX] = useState<number>(0);
    const [mouseY, setMouseY] = useState<number>(0);
    const eyeRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            setMouseX(e.clientX);
            setMouseY(e.clientY);
        };

        window.addEventListener("mousemove", handleMouseMove);

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
        };
    }, []);

    const calculateEyeBallPosition = () => {
        if (!eyeRef.current) return { x: 0, y: 0 };

        if (forceLookX !== undefined && forceLookY !== undefined) {
            return { x: forceLookX, y: forceLookY };
        }

        const eye = eyeRef.current.getBoundingClientRect();
        const eyeCenterX = eye.left + eye.width / 2;
        const eyeCenterY = eye.top + eye.height / 2;

        const deltaX = mouseX - eyeCenterX;
        const deltaY = mouseY - eyeCenterY;
        const distance = Math.min(Math.sqrt(deltaX ** 2 + deltaY ** 2), maxDistance);

        const angle = Math.atan2(deltaY, deltaX);
        const x = Math.cos(angle) * distance;
        const y = Math.sin(angle) * distance;

        return { x, y };
    };

    const pupilPosition = calculateEyeBallPosition();

    return (
        <div
            ref={eyeRef}
            className="rounded-full flex items-center justify-center transition-all duration-150"
            style={{
                width: `${size}px`,
                height: isBlinking ? '2px' : `${size}px`,
                backgroundColor: eyeColor,
                overflow: 'hidden',
            }}
        >
            {!isBlinking && (
                <div
                    className="rounded-full"
                    style={{
                        width: `${pupilSize}px`,
                        height: `${pupilSize}px`,
                        backgroundColor: pupilColor,
                        transform: `translate(${pupilPosition.x}px, ${pupilPosition.y}px)`,
                        transition: 'transform 0.1s ease-out',
                    }}
                />
            )}
        </div>
    );
};


// ============ Main AnimatedLoginPage Component ============
function AnimatedLoginPage() {
    const [showPassword, setShowPassword] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [mouseX, setMouseX] = useState<number>(0);
    const [mouseY, setMouseY] = useState<number>(0);
    const [isPurpleBlinking, setIsPurpleBlinking] = useState(false);
    const [isBlackBlinking, setIsBlackBlinking] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [isLookingAtEachOther, setIsLookingAtEachOther] = useState(false);
    const [isPurplePeeking, setIsPurplePeeking] = useState(false);
    const [showPasswordReset, setShowPasswordReset] = useState(false);
    const [showMailLogin, setShowMailLogin] = useState(false);
    const [showFaceId, setShowFaceId] = useState(false);
    // Аккаунты этого устройства (живые сессии и «Вы вышли»); пока они есть, вместо формы показывается их список
    const [accounts, setAccounts] = useState<DeviceAccountCard[]>([]);
    // «Сменить аккаунт» или вход в аккаунт «Вы вышли»: показать форму входа, не завершая живые сессии
    const [addingAccount, setAddingAccount] = useState(false);
    const [isMailQuickLoggingIn, setIsMailQuickLoggingIn] = useState(false);
    const passwordInputRef = useRef<HTMLInputElement>(null);
    const focusPasswordRef = useRef(false);
    const purpleRef = useRef<HTMLDivElement>(null);
    const blackRef = useRef<HTMLDivElement>(null);
    const yellowRef = useRef<HTMLDivElement>(null);
    const orangeRef = useRef<HTMLDivElement>(null);

    // Интеграция с существующей системой авторизации
    const { login, setLogin, rememberMe, setRememberMe } = useRememberMe();
    const { loading, error, handleLogin, clearError, requiresPasswordChange, pendingLoginData, onPasswordChangeComplete } = useStandardAuth();

    // Авто-конвертер раскладки русской клавиатуры в английскую
    const { handleKeyPress } = useKeyboardLayoutConverter(
        (val) => setEmail(val.toLowerCase()),
        (val) => setPassword(val)
    );

    const navigate = useNavigate();
    const location = useLocation();
    // Выход из аккаунта возвращает сюда с этим флагом: в оставшийся аккаунт сами не входим
    const skipAutoEnter = Boolean((location.state as { skipAutoEnter?: boolean } | null)?.skipAutoEnter);
    const [isValidatingSession, setIsValidatingSession] = useState<boolean>(() => {
        return Boolean(getSessionToken() || readDeviceAccounts().length > 0);
    });
    const isMountedRef = useRef(true);

    const enterAccount = useCallback((account: LiveAccountCard) => {
        switchToDeviceAccount(account);
        navigate(getHomeRoute(account.userData), { replace: true });
    }, [navigate]);

    // Форма входа поверх экрана аккаунтов; для аккаунта «Вы вышли» логин подставлен, курсор сразу в пароле
    const openLoginForm = useCallback((prefillLogin: string) => {
        clearError();
        setEmail(prefillLogin);
        setPassword('');
        focusPasswordRef.current = Boolean(prefillLogin);
        setAddingAccount(true);
    }, [clearError]);

    useEffect(() => {
        if (addingAccount && focusPasswordRef.current) {
            focusPasswordRef.current = false;
            passwordInputRef.current?.focus();
        }
    }, [addingAccount]);

    const handleSelectAccount = useCallback((account: DeviceAccountCard) => {
        if (account.signedOut) {
            openLoginForm(account.username);
        } else {
            enterAccount(account);
        }
    }, [openLoginForm, enterAccount]);

    const handleRemoveAccount = useCallback(async (account: DeviceAccountCard) => {
        await removeDeviceAccount(account);
        setAccounts((prev) => prev.filter((item) => item.userId !== account.userId));
    }, []);

    // Проверяет сессии аккаунтов устройства. Одна живая сессия - сразу в кабинет (если вход не отключен выходом),
    // иначе экран аккаунтов (карточки «Вы вышли» автовходу не мешают), без карточек - форма входа.
    const refreshAccounts = useCallback(async (autoEnter: boolean) => {
        const cards = await loadDeviceAccountCards();
        if (!isMountedRef.current) return;

        const liveCards = cards.filter((card): card is LiveAccountCard => !card.signedOut);
        if (liveCards.length === 1 && autoEnter) {
            enterAccount(liveCards[0]);
            return;
        }
        setAccounts(cards);
        if (cards.length === 0) {
            setAddingAccount(false);
            if (login) setEmail(login);
        }
        setIsValidatingSession(false);
    }, [enterAccount, login]);

    // Проверка сохраненных сессий при открытии портала
    useEffect(() => {
        isMountedRef.current = true;
        refreshAccounts(!skipAutoEnter);
        return () => {
            isMountedRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [login]);

    // Почтовый вход открыл сессии нескольких учетных записей: показываем их список
    const handleAccountsOpened = useCallback(async () => {
        setAddingAccount(false);
        await refreshAccounts(false);
    }, [refreshAccounts]);

    // Быстрый вход через почту в 1 клик (если данные сохранены на этом компьютере)
    const handleMailClick = useCallback(async () => {
        const savedEmail = localStorage.getItem('zerde_saved_mail_email');
        const savedPass = localStorage.getItem('zerde_saved_mail_password');

        if (savedEmail && savedPass) {
            try {
                setIsMailQuickLoggingIn(true);
                const response = await axios.post(`${API_CONFIG.AUTH_V2_URL}/login-via-mail`, {
                    email: savedEmail,
                    mailPassword: savedPass
                });

                if (response.data?.success && response.data?.multipleAccounts && Array.isArray(response.data.accounts)) {
                    rememberDeviceAccounts(response.data.accounts);
                    await handleAccountsOpened();
                    return;
                }

                if (response.data?.success && response.data?.sessionToken) {
                    clearUserSessionFully();
                    saveUserSession(response.data);
                    if (response.data.avatar) {
                        localStorage.setItem('userAvatar', response.data.avatar);
                    }
                    navigate(getHomeRoute(response.data), { replace: true });
                    return;
                }
            } catch {
                // Если пароль устарел или сбой - открываем диалог ввода
            } finally {
                setIsMailQuickLoggingIn(false);
            }
        }

        setShowMailLogin(true);
    }, [navigate, handleAccountsOpened]);


    // Handlers for password reset
    const handleStartPasswordReset = useCallback(() => {
        setShowPasswordReset(true);
    }, []);

    const handleClosePasswordReset = useCallback(() => {
        setShowPasswordReset(false);
    }, []);

    const handlePasswordResetComplete = useCallback((loginValue: string) => {
        setEmail(loginValue);
    }, []);



    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            setMouseX(e.clientX);
            setMouseY(e.clientY);
        };

        window.addEventListener("mousemove", handleMouseMove);
        return () => window.removeEventListener("mousemove", handleMouseMove);
    }, []);

    // Blinking effect for purple character
    useEffect(() => {
        const getRandomBlinkInterval = () => Math.random() * 4000 + 3000;

        const scheduleBlink = () => {
            const blinkTimeout = setTimeout(() => {
                setIsPurpleBlinking(true);
                setTimeout(() => {
                    setIsPurpleBlinking(false);
                    scheduleBlink();
                }, 150);
            }, getRandomBlinkInterval());

            return blinkTimeout;
        };

        const timeout = scheduleBlink();
        return () => clearTimeout(timeout);
    }, []);

    // Blinking effect for black character
    useEffect(() => {
        const getRandomBlinkInterval = () => Math.random() * 4000 + 3000;

        const scheduleBlink = () => {
            const blinkTimeout = setTimeout(() => {
                setIsBlackBlinking(true);
                setTimeout(() => {
                    setIsBlackBlinking(false);
                    scheduleBlink();
                }, 150);
            }, getRandomBlinkInterval());

            return blinkTimeout;
        };

        const timeout = scheduleBlink();
        return () => clearTimeout(timeout);
    }, []);

    // Looking at each other animation when typing starts
    useEffect(() => {
        if (isTyping) {
            setIsLookingAtEachOther(true);
            const timer = setTimeout(() => {
                setIsLookingAtEachOther(false);
            }, 800);
            return () => clearTimeout(timer);
        } else {
            setIsLookingAtEachOther(false);
        }
    }, [isTyping]);

    // Purple sneaky peeking animation when typing password and it's visible
    useEffect(() => {
        if (password.length > 0 && showPassword) {
            const schedulePeek = () => {
                const peekInterval = setTimeout(() => {
                    setIsPurplePeeking(true);
                    setTimeout(() => {
                        setIsPurplePeeking(false);
                    }, 800);
                }, Math.random() * 3000 + 2000);
                return peekInterval;
            };

            const firstPeek = schedulePeek();
            return () => clearTimeout(firstPeek);
        } else {
            setIsPurplePeeking(false);
        }
    }, [password, showPassword, isPurplePeeking]);

    const calculatePosition = (ref: React.RefObject<HTMLDivElement | null>) => {
        if (!ref.current) return { faceX: 0, faceY: 0, bodySkew: 0 };

        const rect = ref.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 3;

        const deltaX = mouseX - centerX;
        const deltaY = mouseY - centerY;

        const faceX = Math.max(-15, Math.min(15, deltaX / 20));
        const faceY = Math.max(-10, Math.min(10, deltaY / 30));
        const bodySkew = Math.max(-6, Math.min(6, -deltaX / 120));

        return { faceX, faceY, bodySkew };
    };

    const purplePos = calculatePosition(purpleRef);
    const blackPos = calculatePosition(blackRef);
    const yellowPos = calculatePosition(yellowRef);
    const orangePos = calculatePosition(orangeRef);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        clearError();
        await handleLogin({ login: email, password }, rememberMe);
    };

    if (isValidatingSession) {
        return (
            <div className="min-h-screen bg-[#f8fafc] flex flex-col items-center justify-center">
                <img src={logo} alt="Zerde" className="h-12 w-auto mb-4 animate-pulse opacity-90" />
                <div className="flex items-center gap-2 text-slate-500 text-sm font-medium">
                    <div className="w-2 h-2 rounded-full bg-blue-600 animate-ping" />
                    Вход в систему...
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen grid lg:grid-cols-2 overflow-hidden">
            {/* Left Content Section - Characters */}
            <div className="relative hidden lg:flex flex-col justify-between bg-gradient-to-br from-primary/90 via-primary to-primary/80 p-12 text-primary-foreground">
                <div className="relative z-20">
                    {/* <img
                        src={logo}
                        alt="Zerde Logo"
                        style={{
                            maxWidth: '200px',
                            height: 'auto',
                            filter: 'drop-shadow(0 2px 8px rgba(99, 102, 241, 0.15))',
                        }}
                    /> */}
                </div>

                <div className="relative z-20 flex items-end justify-center h-[500px]">
                    {/* Cartoon Characters */}
                    <div className="relative" style={{ width: '550px', height: '400px' }}>
                        {/* Purple tall rectangle character - Back layer */}
                        <div
                            ref={purpleRef}
                            className="absolute bottom-0 transition-all duration-700 ease-in-out"
                            style={{
                                left: '70px',
                                width: '180px',
                                height: (isTyping || (password.length > 0 && !showPassword)) ? '440px' : '400px',
                                backgroundColor: 'rgb(209 54 72)',
                                borderRadius: '10px 10px 0 0',
                                zIndex: 1,
                                transform: (password.length > 0 && showPassword)
                                    ? `skewX(0deg)`
                                    : (isTyping || (password.length > 0 && !showPassword))
                                        ? `skewX(${(purplePos.bodySkew || 0) - 12}deg) translateX(40px)`
                                        : `skewX(${purplePos.bodySkew || 0}deg)`,
                                transformOrigin: 'bottom center',
                            }}
                        >
                            {/* Eyes */}
                            <div
                                className="absolute flex gap-8 transition-all duration-700 ease-in-out"
                                style={{
                                    left: (password.length > 0 && showPassword) ? `${20}px` : isLookingAtEachOther ? `${55}px` : `${45 + purplePos.faceX}px`,
                                    top: (password.length > 0 && showPassword) ? `${35}px` : isLookingAtEachOther ? `${65}px` : `${40 + purplePos.faceY}px`,
                                }}
                            >
                                <EyeBall
                                    size={18}
                                    pupilSize={7}
                                    maxDistance={5}
                                    eyeColor="white"
                                    pupilColor="#2D2D2D"
                                    isBlinking={isPurpleBlinking}
                                    forceLookX={(password.length > 0 && showPassword) ? (isPurplePeeking ? 4 : -4) : isLookingAtEachOther ? 3 : undefined}
                                    forceLookY={(password.length > 0 && showPassword) ? (isPurplePeeking ? 5 : -4) : isLookingAtEachOther ? 4 : undefined}
                                />
                                <EyeBall
                                    size={18}
                                    pupilSize={7}
                                    maxDistance={5}
                                    eyeColor="white"
                                    pupilColor="#2D2D2D"
                                    isBlinking={isPurpleBlinking}
                                    forceLookX={(password.length > 0 && showPassword) ? (isPurplePeeking ? 4 : -4) : isLookingAtEachOther ? 3 : undefined}
                                    forceLookY={(password.length > 0 && showPassword) ? (isPurplePeeking ? 5 : -4) : isLookingAtEachOther ? 4 : undefined}
                                />
                            </div>
                        </div>

                        {/* Black tall rectangle character - Middle layer */}
                        <div
                            ref={blackRef}
                            className="absolute bottom-0 transition-all duration-700 ease-in-out"
                            style={{
                                left: '240px',
                                width: '120px',
                                height: '310px',
                                backgroundColor: 'rgb(109 214 145)',
                                borderRadius: '8px 8px 0 0',
                                zIndex: 2,
                                transform: (password.length > 0 && showPassword)
                                    ? `skewX(0deg)`
                                    : isLookingAtEachOther
                                        ? `skewX(${(blackPos.bodySkew || 0) * 1.5 + 10}deg) translateX(20px)`
                                        : (isTyping || (password.length > 0 && !showPassword))
                                            ? `skewX(${(blackPos.bodySkew || 0) * 1.5}deg)`
                                            : `skewX(${blackPos.bodySkew || 0}deg)`,
                                transformOrigin: 'bottom center',
                            }}
                        >
                            {/* Eyes */}
                            <div
                                className="absolute flex gap-6 transition-all duration-700 ease-in-out"
                                style={{
                                    left: (password.length > 0 && showPassword) ? `${10}px` : isLookingAtEachOther ? `${32}px` : `${26 + blackPos.faceX}px`,
                                    top: (password.length > 0 && showPassword) ? `${28}px` : isLookingAtEachOther ? `${12}px` : `${32 + blackPos.faceY}px`,
                                }}
                            >
                                <EyeBall
                                    size={16}
                                    pupilSize={6}
                                    maxDistance={4}
                                    eyeColor="white"
                                    pupilColor="#2D2D2D"
                                    isBlinking={isBlackBlinking}
                                    forceLookX={(password.length > 0 && showPassword) ? -4 : isLookingAtEachOther ? 0 : undefined}
                                    forceLookY={(password.length > 0 && showPassword) ? -4 : isLookingAtEachOther ? -4 : undefined}
                                />
                                <EyeBall
                                    size={16}
                                    pupilSize={6}
                                    maxDistance={4}
                                    eyeColor="white"
                                    pupilColor="#2D2D2D"
                                    isBlinking={isBlackBlinking}
                                    forceLookX={(password.length > 0 && showPassword) ? -4 : isLookingAtEachOther ? 0 : undefined}
                                    forceLookY={(password.length > 0 && showPassword) ? -4 : isLookingAtEachOther ? -4 : undefined}
                                />
                            </div>
                        </div>

                        {/* Orange semi-circle character - Front left */}
                        <div
                            ref={orangeRef}
                            className="absolute bottom-0 transition-all duration-700 ease-in-out"
                            style={{
                                left: '0px',
                                width: '240px',
                                height: '200px',
                                zIndex: 3,
                                backgroundColor: 'rgb(248 144 45)',
                                borderRadius: '120px 120px 0 0',
                                transform: (password.length > 0 && showPassword) ? `skewX(0deg)` : `skewX(${orangePos.bodySkew || 0}deg)`,
                                transformOrigin: 'bottom center',
                            }}
                        >
                            {/* Eyes - just pupils, no white */}
                            <div
                                className="absolute flex gap-8 transition-all duration-200 ease-out"
                                style={{
                                    left: (password.length > 0 && showPassword) ? `${50}px` : `${82 + (orangePos.faceX || 0)}px`,
                                    top: (password.length > 0 && showPassword) ? `${85}px` : `${90 + (orangePos.faceY || 0)}px`,
                                }}
                            >
                                <Pupil size={12} maxDistance={5} pupilColor="#2D2D2D" forceLookX={(password.length > 0 && showPassword) ? -5 : undefined} forceLookY={(password.length > 0 && showPassword) ? -4 : undefined} />
                                <Pupil size={12} maxDistance={5} pupilColor="#2D2D2D" forceLookX={(password.length > 0 && showPassword) ? -5 : undefined} forceLookY={(password.length > 0 && showPassword) ? -4 : undefined} />
                            </div>
                        </div>

                        {/* Yellow tall rectangle character - Front right */}
                        <div
                            ref={yellowRef}
                            className="absolute bottom-0 transition-all duration-700 ease-in-out"
                            style={{
                                left: '310px',
                                width: '140px',
                                height: '230px',
                                backgroundColor: 'rgb(246 185 68)',
                                borderRadius: '70px 70px 0 0',
                                zIndex: 4,
                                transform: (password.length > 0 && showPassword) ? `skewX(0deg)` : `skewX(${yellowPos.bodySkew || 0}deg)`,
                                transformOrigin: 'bottom center',
                            }}
                        >
                            {/* Eyes - just pupils, no white */}
                            <div
                                className="absolute flex gap-6 transition-all duration-200 ease-out"
                                style={{
                                    left: (password.length > 0 && showPassword) ? `${20}px` : `${52 + (yellowPos.faceX || 0)}px`,
                                    top: (password.length > 0 && showPassword) ? `${35}px` : `${40 + (yellowPos.faceY || 0)}px`,
                                }}
                            >
                                <Pupil size={12} maxDistance={5} pupilColor="#2D2D2D" forceLookX={(password.length > 0 && showPassword) ? -5 : undefined} forceLookY={(password.length > 0 && showPassword) ? -4 : undefined} />
                                <Pupil size={12} maxDistance={5} pupilColor="#2D2D2D" forceLookX={(password.length > 0 && showPassword) ? -5 : undefined} forceLookY={(password.length > 0 && showPassword) ? -4 : undefined} />
                            </div>
                            {/* Horizontal line for mouth */}
                            <div
                                className="absolute w-20 h-[4px] bg-[#2D2D2D] rounded-full transition-all duration-200 ease-out"
                                style={{
                                    left: (password.length > 0 && showPassword) ? `${10}px` : `${40 + (yellowPos.faceX || 0)}px`,
                                    top: (password.length > 0 && showPassword) ? `${88}px` : `${88 + (yellowPos.faceY || 0)}px`,
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div className="relative z-20 flex items-center gap-8 text-sm text-primary-foreground/60">
                </div>

                {/* Background */}
                <div className="absolute inset-0" style={{ background: 'rgb(25 118 210)' }} />

                {/* Blur decorations */}
                <div className="absolute top-1/4 right-1/4 size-64 bg-primary-foreground/10 rounded-full blur-3xl" style={{ zIndex: 5 }} />
                <div className="absolute bottom-1/4 left-1/4 size-96 bg-primary-foreground/5 rounded-full blur-3xl" style={{ zIndex: 5 }} />
            </div>

            {/* Right Login Section - Dark Theme */}
            <div className="flex items-center justify-center p-8 bg-black dark">
                <div className="w-full max-w-[420px]">
                    {requiresPasswordChange ? (
                        /* Password Change Form */
                        <PasswordChangeForm
                            sessionToken={pendingLoginData?.response.sessionToken || ''}
                            currentPassword={password}
                            onSuccess={onPasswordChangeComplete}
                        />
                    ) : accounts.length > 0 && !addingAccount ? (
                        /* Аккаунты устройства: карточки вместо формы */
                        <ActiveAccountsPanel
                            accounts={accounts}
                            onSelect={handleSelectAccount}
                            onRemove={handleRemoveAccount}
                            onChangeAccount={() => openLoginForm('')}
                        />
                    ) : (
                        /* Login Form */
                        <>
                            {/* Header */}
                            <div className="text-center mb-10">
                                <h1 className="text-3xl font-bold tracking-tight mb-2 text-white">Добро пожаловать!</h1>
                                <p className="text-gray-400 text-sm">Пожалуйста, введите данные для входа</p>
                            </div>

                            {/* Login Form */}
                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-sm font-medium text-gray-300">Логин или корпоративная почта</Label>
                                    <Input
                                        id="email"
                                        name="login"
                                        type="text"
                                        placeholder="ap8899 или ap8899@zerdegroup.kz"
                                        value={email}
                                        autoComplete="off"
                                        onChange={(e) => setEmail(e.target.value.toLowerCase())}
                                        onKeyDown={handleKeyPress}
                                        onFocus={() => setIsTyping(true)}
                                        onBlur={() => setIsTyping(false)}
                                        required
                                        className="h-12 bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 focus:border-primary"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="password" className="text-sm font-medium text-gray-300">Пароль</Label>
                                    <div className="relative">
                                        <Input
                                            ref={passwordInputRef}
                                            id="password"
                                            name="password"
                                            type={showPassword ? "text" : "password"}
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            onKeyDown={handleKeyPress}
                                            required
                                            className="h-12 pr-10 bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 focus:border-primary"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                                        >
                                            {showPassword ? (
                                                <EyeOff className="size-5" />
                                            ) : (
                                                <Eye className="size-5" />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            id="remember"
                                            checked={rememberMe}
                                            onCheckedChange={(checked) => setRememberMe(checked === true)}
                                            className="border-gray-600 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                                        />
                                        <Label
                                            htmlFor="remember"
                                            className="text-sm font-normal cursor-pointer text-gray-300"
                                        >
                                            Запомнить логин
                                        </Label>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleStartPasswordReset}
                                        className="text-sm text-primary hover:underline font-medium"
                                    >
                                        Забыли пароль?
                                    </button>
                                </div>

                                {error && (
                                    <div className="p-3 text-sm text-red-400 bg-red-950/20 border border-red-900/30 rounded-lg">
                                        {error}
                                    </div>
                                )}

                                <div className="flex items-center gap-2.5">
                                    <Button
                                        type="submit"
                                        className="flex-1 h-12 text-base font-medium bg-gray-100 text-black hover:bg-white transition-all shadow-md active:scale-[0.99]"
                                        size="lg"
                                        disabled={loading}
                                    >
                                        {loading ? "Вход..." : "Войти"}
                                    </Button>

                                    {/* Кнопка 1: Почта (@zerdegroup.kz) */}
                                    <Tooltip title={localStorage.getItem('zerde_saved_mail_email') ? `Быстрый вход через почту (${localStorage.getItem('zerde_saved_mail_email')})` : "Вход через корпоративную почту (@zerdegroup.kz)"} arrow placement="top">
                                        <button
                                            type="button"
                                            onClick={handleMailClick}
                                            disabled={isMailQuickLoggingIn}
                                            className="flex items-center justify-center h-12 w-12 rounded-xl bg-gray-900 border border-gray-700 hover:border-primary hover:bg-gray-800 text-gray-300 hover:text-white transition-all shadow-md group shrink-0 focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer disabled:opacity-60"
                                        >
                                            {isMailQuickLoggingIn ? (
                                                <CircularProgress size={18} sx={{ color: '#818cf8' }} />
                                            ) : (
                                                <Mail className="size-5 transition-transform group-hover:scale-110" />
                                            )}
                                        </button>
                                    </Tooltip>

                                    {/* Кнопка 2: FaceID (Биометрия) */}
                                    <Tooltip title="Вход по FaceID (Биометрия)" arrow placement="top">
                                        <button
                                            type="button"
                                            onClick={() => setShowFaceId(true)}
                                            className="flex items-center justify-center h-12 w-12 rounded-xl bg-gray-900 border border-gray-700 hover:border-emerald-500 hover:bg-gray-800 text-gray-300 hover:text-emerald-400 transition-all shadow-md group shrink-0 focus:outline-none focus:ring-2 focus:ring-emerald-500 cursor-pointer"
                                        >
                                            <ScanFace className="size-5 transition-transform group-hover:scale-110" />
                                        </button>
                                    </Tooltip>
                                </div>

                                {/* Есть аккаунты устройства: вернуться к их списку */}
                                {accounts.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => setAddingAccount(false)}
                                        className="w-full text-center text-xs text-gray-400 hover:text-white transition-colors underline decoration-dotted cursor-pointer"
                                    >
                                        Вернуться к аккаунтам
                                    </button>
                                )}
                            </form>

                            {/* Password Reset Dialog */}
                            <PasswordResetDialogWithWA
                                open={showPasswordReset}
                                onClose={handleClosePasswordReset}
                                onComplete={handlePasswordResetComplete}
                            />

                            {/* Mail Login Dialog */}
                            <MailLoginDialog
                                open={showMailLogin}
                                onClose={() => setShowMailLogin(false)}
                                onSuccess={(userData) => {
                                    navigate(getHomeRoute(userData), { replace: true });
                                }}
                                onAccountsOpened={handleAccountsOpened}
                            />

                            {/* FaceID Dialog */}
                            <FaceIdDialog
                                open={showFaceId}
                                onClose={() => setShowFaceId(false)}
                                onSuccess={(userData) => {
                                    const targetRoute = userData.targetInterface === 'admin' || (!userData.targetInterface && ['superadmin', 'admin', 'moder', 'view', 'guide', 'km', 'to', 'full-report'].includes(userData.role))
                                        ? '/admin'
                                        : '/dashboard';
                                    navigate(targetRoute, { replace: true });
                                }}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

// ============ Inline Password Change Form Component ============
interface PasswordChangeFormProps {
    /** Токен сессии, созданной входом: сессия еще не сохранена в localStorage */
    sessionToken: string;
    currentPassword: string;
    onSuccess: () => void;
}

function PasswordChangeForm({ sessionToken, currentPassword, onSuccess }: PasswordChangeFormProps) {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const validatePassword = (password: string): string | null => {
        if (password.length < 6) {
            return 'Пароль должен содержать минимум 6 символов';
        }
        const englishPattern = /^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]*$/;
        if (!englishPattern.test(password)) {
            return 'Пароль должен содержать только английские символы';
        }
        return null;
    };

    const canSubmit = () => {
        if (!newPassword || !confirmPassword) return false;
        if (newPassword === currentPassword) return false;
        if (newPassword !== confirmPassword) return false;
        if (validatePassword(newPassword)) return false;
        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (newPassword === currentPassword) {
            setError('Новый пароль должен отличаться от текущего');
            return;
        }

        const passwordError = validatePassword(newPassword);
        if (passwordError) {
            setError(passwordError);
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Пароли не совпадают');
            return;
        }

        setLoading(true);

        try {
            // Смена пароля своей сессией с проверкой текущего пароля (у подчиненного точки - свой логин и пароль).
            // Заголовок x-session-token перехватчик axios не перезаписывает токеном прежней учетной записи из localStorage
            const response = await axios.post(
                `${API_CONFIG.BASE_URL}/v2/profile/password`,
                { currentPassword, newPassword },
                { headers: { Authorization: `Bearer ${sessionToken}`, 'x-session-token': sessionToken } }
            );

            if (response.data.success) {
                onSuccess();
            } else {
                setError(response.data.message || 'Ошибка при смене пароля');
            }
        } catch (err: any) {
            console.error('Error changing password:', err);
            setError(err.response?.data?.message || 'Ошибка сервера при смене пароля');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {/* Header */}
            <div className="text-center mb-10">
                <div className="flex items-center justify-center gap-2 mb-2">
                    <h1 className="text-3xl font-bold tracking-tight text-white">Смена пароля</h1>
                    <div className="relative group">
                        <HelpCircle className="size-5 text-gray-500 cursor-help hover:text-primary transition-colors" />
                        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-64 p-3 bg-gray-800 text-gray-200 text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                            Ваш пароль устарел или используется впервые. Для безопасности необходимо установить новый пароль.
                            <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-gray-800 rotate-45"></div>
                        </div>
                    </div>
                </div>
                <p className="text-gray-400 text-sm">Установите новый пароль для продолжения</p>
            </div>

            {/* Password Change Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                    <Label htmlFor="newPassword" className="text-sm font-medium text-gray-300">Новый пароль</Label>
                    <div className="relative">
                        <Input
                            id="newPassword"
                            type={showNewPassword ? "text" : "password"}
                            placeholder="••••••••"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                            className="h-12 pr-10 bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 focus:border-primary"
                        />
                        <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                        >
                            {showNewPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500">Минимум 6 символов, только английские буквы и цифры</p>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-300">Подтвердите пароль</Label>
                    <div className="relative">
                        <Input
                            id="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="••••••••"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            className="h-12 pr-10 bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 focus:border-primary"
                        />
                        <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                        >
                            {showConfirmPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="p-3 text-sm text-red-400 bg-red-950/20 border border-red-900/30 rounded-lg">
                        {error}
                    </div>
                )}

                <Button
                    type="submit"
                    className="w-full h-12 text-base font-medium bg-gray-100 text-black hover:bg-white"
                    size="lg"
                    disabled={loading || !canSubmit()}
                >
                    {loading ? "Смена пароля..." : "Сменить пароль и войти"}
                </Button>
            </form>
        </>
    );
}

export default AnimatedLoginPage;
